import PropTypes from "prop-types";
import {
  BLINK_SHAPES,
  DEFAULT_BLINK_SHAPE,
  DEFAULT_BLINK_SPEED,
  FIXED_BLINK_SHAPE,
  colorVectorToHex,
  getDefaultHologramState,
  hexToColorVector,
  normalizeBlinkColor,
  parseBlinkSpeed,
} from "../utils/hologramState";

/**
 * Champs de clignotement d'un hologramme :
 * - sélecteur "Clignotement" (Off, Fixed, Square, Sine, Triangle, Sawtooth) ;
 * - "Vitesse de clignotement" pour les vrais clignotements (ni Off ni Fixed) ;
 * - "Couleur de clignotement" (sélecteur de couleur => vector3 rgb 0-1) pour toutes les formes sauf Off.
 * `value` : { blinkShape, blinkSpeed, blinkColorRGB } (blinkSpeed peut être une chaîne en cours de saisie).
 * `onChange` reçoit le nouvel état complet.
 * À afficher uniquement quand un hologramme est présent (fichier choisi ou enregistré).
 */
const HologramBlinkFields = ({
  value,
  onChange,
  shapeTitle = "Type de clignotement des partie emissive de l'hologramme",
  speedTitle = "Vitesse de clignotement (nombre strictement positif)",
  colorTitle = "Couleur de clignotement des parties émissives de l'hologramme",
}) => {
  const shape = BLINK_SHAPES.includes(value?.blinkShape) ? value.blinkShape : DEFAULT_BLINK_SHAPE;
  const speed = value?.blinkSpeed ?? DEFAULT_BLINK_SPEED;
  const color = normalizeBlinkColor(value?.blinkColorRGB);
  const speedNumber = parseBlinkSpeed(speed);
  const speedInvalid = speedNumber === null || speedNumber <= 0;
  const isActive = shape !== DEFAULT_BLINK_SHAPE;
  const isBlinking = isActive && shape !== FIXED_BLINK_SHAPE;

  const handleShapeChange = (e) => {
    const newShape = e.target.value;
    if (newShape === DEFAULT_BLINK_SHAPE) {
      // Retour sur Off : vitesse et couleur remises à leur valeur par défaut
      onChange(getDefaultHologramState());
    } else if (newShape === FIXED_BLINK_SHAPE) {
      // Fixed : couleur constante, vitesse forcée à sa valeur par défaut
      onChange({ blinkShape: newShape, blinkSpeed: DEFAULT_BLINK_SPEED, blinkColorRGB: color });
    } else {
      onChange({ blinkShape: newShape, blinkSpeed: speed, blinkColorRGB: color });
    }
  };

  return (
    <div alt="clignotement de l'hologramme" className="mt-2">
      <label className="form-label small mb-0" title={shapeTitle}>Clignotement ℹ️</label>
      <select
        className="form-select form-select-sm"
        value={shape}
        title={shapeTitle}
        onChange={handleShapeChange}
      >
        {BLINK_SHAPES.map((blinkShape) => (
          <option key={blinkShape} value={blinkShape}>{blinkShape}</option>
        ))}
      </select>
      {isBlinking && (
        <div className="mt-2">
          <label className="form-label small mb-0" title={speedTitle}>Vitesse de clignotement ℹ️</label>
          <input
            type="text"
            inputMode="decimal"
            className={`form-control form-control-sm${speedInvalid ? " is-invalid" : ""}`}
            value={String(speed)}
            title={speedTitle}
            onChange={(e) => onChange({ blinkShape: shape, blinkSpeed: e.target.value, blinkColorRGB: color })}
          />
        </div>
      )}
      {isActive && (
        <div className="mt-2">
          <label className="form-label small mb-0" title={colorTitle}>Couleur de clignotement ℹ️</label>
          <input
            type="color"
            className="form-control form-control-color form-control-sm"
            value={colorVectorToHex(color)}
            title={colorTitle}
            onChange={(e) => onChange({
              blinkShape: shape,
              blinkSpeed: isBlinking ? speed : DEFAULT_BLINK_SPEED,
              blinkColorRGB: hexToColorVector(e.target.value),
            })}
          />
        </div>
      )}
    </div>
  );
};

HologramBlinkFields.propTypes = {
  value: PropTypes.shape({
    blinkShape: PropTypes.string,
    blinkSpeed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    blinkColorRGB: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number, z: PropTypes.number }),
  }),
  onChange: PropTypes.func.isRequired,
  shapeTitle: PropTypes.string,
  speedTitle: PropTypes.string,
  colorTitle: PropTypes.string,
};

export default HologramBlinkFields;
